import React, { useContext } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import CoreBadge from '@wix/da-ds/pkg/Badges/CoreBadge';
import BetaBadge from '@wix/da-ds/pkg/BetaBadge';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { URLS } from '@wix/da-url';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { AddWidgetBiEvent, BiData, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { InstallableWidget } from '../../../types/modules';
import { ZoneName } from '../../../types/zones';
import { useLayoutActions } from '../../../hooks/useLayoutActions';
import { useSelector } from 'react-redux';
import { getModuleCount } from '../../../redux/selectors/modules';
import useGruser from '../../../hooks/useGruser';
import { useCurrentZoneName } from '../../../hooks/useZone';
import { moduleTypeToReferralType } from '../helpers';

import s from './WidgetTypeCard.scss';

export interface Props {
  position: number;
  installableWidget: InstallableWidget;
  className?: string;
}

const WidgetTypeCard: React.FC<Props> = ({ className, position, installableWidget }) => {
  const { t } = useTranslation();
  const { addModule } = useLayoutActions();
  const zoneFromContext = useCurrentZoneName();
  const logBiEvent = useBiLogger();
  const { hasCoreMembership } = useContext(SessionContext);
  const { gruser } = useGruser();

  const {
    module: { moduleName, restrict, biReferralType, zone = zoneFromContext },
    widgetCard,
  } = installableWidget;

  const installedCount = useSelector<any>(state =>
    getModuleCount(state, gruser, zone, moduleName)
  ) as number;

  if (!widgetCard || !addModule || !zone) {
    return null;
  }

  const { title, description, minuatureImageSrc, isNew, isDecoratedWithStars } = widgetCard;
  const coreLimit = restrict && restrict.priv === 'core' ? restrict.limit : 0;
  const isCoreOnlyModule = !!coreLimit;
  const needsCoreForThis =
    isCoreOnlyModule && !hasCoreMembership && installedCount >= (restrict?.limit || 0);

  return (
    <div
      className={c(
        s['root'],
        isNew && s['new'],
        isCoreOnlyModule && s['core-only'],
        isDecoratedWithStars && s['bg-with-stars'],
        className
      )}
      onClick={() => {
        if (needsCoreForThis) {
          const referral_info = biReferralType || moduleTypeToReferralType(moduleName);
          logBiEvent(
            BiData<BuyCoreButtonBiEvent>({
              evid: 375,
              referral_info: referral_info as any,
            })
          );
          window && window.open(URLS.CORE_MEMBERSHIP, '_blank');
          return;
        }

        addModule(moduleName, zone as ZoneName, position);

        logBiEvent(
          BiData<AddWidgetBiEvent>({
            evid: 283,
            widgetname: moduleName as string,
            widgetzone: zone as string,
          })
        );
      }}
    >
      <div className={c(s['minuature-wrapper'])}>
        <div className={s['miniature']} style={{ backgroundImage: `url(${minuatureImageSrc})` }} />
      </div>
      <div className={s['title']}>
        {title}
        {isNew && (
          <BetaBadge className={s['new-badge']} size="small" badgeType="new" variant="green" />
        )}
      </div>
      <div className={s['description']}>{description}</div>
      {needsCoreForThis && (
        <div className={s['core-upsell-overlay']}>
          <div className={s['core-upsell-overlay-text']}>
            {t('gruser.widgetbox.coreUpsell.becomeCore.toUnlock', {
              postProcess: ['reactPostprocessor', 'featureBrancher'],
              count: coreLimit,
              coreBadge: <CoreBadge size="small" className={s['core-badge']} />,
              coreLimit,
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetTypeCard;
